// pages/Budget.jsx - Zero-based budgeting (YNAB-style)
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_PATH from '../apiConfig';
import { formatCurrency } from '../utils';
import AddBudgetModal from '../components/AddBudgetModal';
import { showToast } from '../components/Toast';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Category Component
const SortableCategory = ({ category, children, isEditing, editContent, normalContent, isSelected }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? 'var(--primary-light)' : (isSelected ? 'var(--primary-light)' : 'var(--bg-secondary)'),
  };

  return (
    <>
      <tr
        ref={setNodeRef}
        style={style}
        {...attributes}
      >
        <td colSpan="5" style={{ padding: '6px 8px', fontWeight: 600, fontSize: '13px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {!isEditing && (
              <div
                {...listeners}
                style={{
                  cursor: 'grab',
                  padding: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--text-secondary)',
                }}
                title="Drag to reorder"
              >
                <i className="fas fa-grip-vertical" style={{ fontSize: '11px' }}></i>
              </div>
            )}
            <div style={{ flex: 1 }}>
              {isEditing ? editContent : normalContent}
            </div>
          </div>
        </td>
      </tr>
      {children}
    </>
  );
};

const Budget = () => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const [incomeTransactions, setIncomeTransactions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [preselectedCategoryId, setPreselectedCategoryId] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningBudget, setAssigningBudget] = useState(null);
  const [assignAmount, setAssignAmount] = useState('');

  // Inline category editing
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [editingCategoryIcon, setEditingCategoryIcon] = useState('fa-folder');
  const [isAddingCategory, setIsAddingCategory] = useState(false);

  // Popup menu state
  const [popupMenu, setPopupMenu] = useState(null); // { type: 'category'|'budget', id: string, x: number, y: number }

  // Selected row for summary panel
  const [selectedRow, setSelectedRow] = useState(null); // { type: 'category'|'budget', item: object }

  // Hover state for category to show + button
  const [hoveredCategoryId, setHoveredCategoryId] = useState(null);


  useEffect(() => {
    fetchData();
  }, [currentMonth]);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setPopupMenu(null);
    if (popupMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [popupMenu]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Calculate start and end dates for the month
      const [year, month] = currentMonth.split('-');
      const startDate = `${year}-${month}-01`;
      const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
      const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;

      const [monthlyData, transactionsData, categoriesData, debtsData] = await Promise.all([
        axios.get(`${API_BASE_PATH}/budget-monthly`, { params: { month: currentMonth } }),
        axios.get(`${API_BASE_PATH}/transactions`, { params: { start_date: startDate, end_date: endDate } }),
        axios.get(`${API_BASE_PATH}/budget-categories`),
        axios.get(`${API_BASE_PATH}/debts`)
      ]);

      const items = monthlyData.data.items || [];
      const allTransactions = transactionsData.data || [];
      const allDebts = debtsData.data || [];

      // Get income transactions for the month
      const incomeTransactions = allTransactions.filter(t => t.type === 'income');
      setIncomeTransactions(incomeTransactions);
      setTransactions(allTransactions);

      // Only show budget items
      setBudgets(items.filter(i => i.type === 'budget'));

      // Store debts
      setDebts(allDebts);

      // Get or create Debt Payments category
      let debtPaymentsCategory = categoriesData.data?.find(c => c.name === 'Debt Payments');

      // Add debt payments category to categories (will be shown with virtual budgets)
      const categoriesWithDebts = [...(categoriesData.data || [])];

      // Only add virtual debt payments category if there are debts
      if (allDebts.length > 0 && !debtPaymentsCategory) {
        // Create a virtual category for debt payments (not persisted to DB)
        debtPaymentsCategory = {
          id: 'virtual-debt-payments',
          name: 'Debt Payments',
          icon: 'fa-credit-card',
          sort_order: 999, // Put at the end
          isVirtual: true
        };
        categoriesWithDebts.push(debtPaymentsCategory);
      }

      setCategories(categoriesWithDebts);
    } catch (error) {
      console.error('Error fetching budget data:', error);
      showToast('Failed to load budget data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBudget = async (budgetId) => {
    // Prevent deleting virtual debt payment budgets
    if (budgetId && budgetId.toString().startsWith('virtual-debt-')) {
      showToast('Cannot delete virtual debt payment budget. This budget is automatically created from your debts.', 'error');
      return;
    }

    if (!confirm('Delete this budget?')) return;

    try {
      await axios.delete(`${API_BASE_PATH}/budget-items/${budgetId}`);
      showToast('Budget deleted', 'success');
      fetchData();
    } catch (error) {
      console.error('Error deleting budget:', error);
      showToast('Failed to delete budget', 'error');
    }
  };

  const handleAssignMoney = async () => {
    if (!assigningBudget || !assignAmount) return;

    try {
      const newAssignedAmount = (assigningBudget.assigned_amount || 0) + parseFloat(assignAmount);

      await axios.post(`${API_BASE_PATH}/budget-items/${assigningBudget.id}/assign`, {
        assigned_amount: newAssignedAmount,
        month: currentMonth
      });

      showToast('Money assigned successfully', 'success');
      setShowAssignModal(false);
      setAssigningBudget(null);
      setAssignAmount('');
      fetchData();
    } catch (error) {
      console.error('Error assigning money:', error);
      showToast('Failed to assign money', 'error');
    }
  };

  const handleAddCategory = () => {
    setIsAddingCategory(true);
    setEditingCategoryName('');
    setEditingCategoryIcon('fa-folder');
  };

  const handleSaveNewCategory = async () => {
    if (!editingCategoryName.trim()) {
      showToast('Category name required', 'error');
      return;
    }

    try {
      await axios.post(`${API_BASE_PATH}/budget-categories`, {
        name: editingCategoryName.trim(),
        icon: editingCategoryIcon,
        sort_order: categories.length
      });

      showToast('Category created', 'success');
      setIsAddingCategory(false);
      setEditingCategoryName('');
      setEditingCategoryIcon('fa-folder');
      fetchData();
    } catch (error) {
      console.error('Error creating category:', error);
      showToast('Failed to create category', 'error');
    }
  };

  const handleCancelAddCategory = () => {
    setIsAddingCategory(false);
    setEditingCategoryName('');
    setEditingCategoryIcon('fa-folder');
  };

  const handleEditCategory = (category) => {
    // Prevent editing virtual debt payments category
    if (category.id === 'virtual-debt-payments') {
      showToast('Cannot edit Debt Payments category. This category is automatically managed.', 'error');
      return;
    }

    setEditingCategoryId(category.id);
    setEditingCategoryName(category.name);
    setEditingCategoryIcon(category.icon || 'fa-folder');
  };

  const handleSaveCategory = async (categoryId) => {
    if (!editingCategoryName.trim()) {
      showToast('Category name required', 'error');
      return;
    }

    try {
      await axios.put(`${API_BASE_PATH}/budget-categories/${categoryId}`, {
        name: editingCategoryName.trim(),
        icon: editingCategoryIcon
      });

      showToast('Category updated', 'success');
      setEditingCategoryId(null);
      setEditingCategoryName('');
      setEditingCategoryIcon('fa-folder');
      fetchData();
    } catch (error) {
      console.error('Error updating category:', error);
      showToast('Failed to update category', 'error');
    }
  };

  const handleCancelEditCategory = () => {
    setEditingCategoryId(null);
    setEditingCategoryName('');
    setEditingCategoryIcon('fa-folder');
  };

  const handleDeleteCategory = async (categoryId) => {
    // Prevent deleting virtual debt payments category
    if (categoryId === 'virtual-debt-payments') {
      showToast('Cannot delete Debt Payments category. This category is automatically created for your debts.', 'error');
      return;
    }

    const categoryBudgets = budgets.filter(b => b.category_id === categoryId);

    if (categoryBudgets.length > 0) {
      showToast('Cannot delete category with budgets. Move or delete budgets first.', 'error');
      return;
    }

    if (!confirm('Delete this category?')) return;

    try {
      await axios.delete(`${API_BASE_PATH}/budget-categories/${categoryId}`);
      showToast('Category deleted', 'success');
      setPopupMenu(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting category:', error);
      showToast('Failed to delete category', 'error');
    }
  };

  const handleCategoryClick = (e, category) => {
    e.stopPropagation();
    setSelectedRow({ type: 'category', item: category });
  };

  const handleBudgetClick = (e, budget) => {
    e.stopPropagation();
    setSelectedRow({ type: 'budget', item: budget });
  };

  const handleCreateBudgetForCategory = (e, categoryId) => {
    e.stopPropagation();
    setPreselectedCategoryId(categoryId);
    setShowBudgetModal(true);
  };

  const openAssignModal = (budget) => {
    setAssigningBudget(budget);
    setAssignAmount('');
    setShowAssignModal(true);
  };

  const handleQuickAssign = async (budget, amount) => {
    if (amount <= 0) {
      showToast('Invalid amount', 'error');
      return;
    }

    if (amount > readyToAssign) {
      showToast(`Not enough funds. Need ${formatCurrency(amount)} but only ${formatCurrency(readyToAssign)} available.`, 'error');
      return;
    }

    try {
      const newAssignedAmount = (budget.assigned_amount || 0) + amount;

      await axios.post(`${API_BASE_PATH}/budget-items/${budget.id}/assign`, {
        assigned_amount: newAssignedAmount,
        month: currentMonth
      });

      showToast(`Assigned ${formatCurrency(amount)}`, 'success');
      fetchData();
    } catch (error) {
      console.error('Error assigning money:', error);
      showToast('Failed to assign money', 'error');
    }
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = categories.findIndex((cat) => cat.id === active.id);
      const newIndex = categories.findIndex((cat) => cat.id === over.id);

      const newCategories = arrayMove(categories, oldIndex, newIndex);

      // Optimistically update the UI
      setCategories(newCategories);

      // Update sort_order for all affected categories in the backend
      try {
        // Update each category's sort_order
        await Promise.all(
          newCategories.map((category, index) =>
            axios.put(`${API_BASE_PATH}/budget-categories/${category.id}`, {
              sort_order: index
            })
          )
        );
      } catch (error) {
        console.error('Error updating category order:', error);
        showToast('Failed to update category order', 'error');
        // Revert to original order on error
        fetchData();
      }
    }
  };

  const handlePrevMonth = () => {
    const [year, month] = currentMonth.split('-').map(Number);
    const newDate = new Date(year, month - 2, 1);
    setCurrentMonth(`${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`);
  };

  const handleNextMonth = () => {
    const [year, month] = currentMonth.split('-').map(Number);
    const newDate = new Date(year, month, 1);
    setCurrentMonth(`${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`);
  };

  const handleToday = () => {
    const now = new Date();
    setCurrentMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  };

  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Calculate total income for the month
  const totalIncome = incomeTransactions.reduce((sum, t) => sum + t.amount, 0);

  // Calculate total assigned across all budgets
  const totalAssigned = budgets.reduce((sum, b) => sum + (b.assigned_amount || 0), 0);

  // Calculate Ready to Assign (income minus assigned)
  const readyToAssign = totalIncome - totalAssigned;

  // Create virtual budgets for all debts
  const debtVirtualBudgets = debts.map(debt => {
    // Find payment transactions for this debt in the current month
    // Only payments count as activity - expenses on the debt are budgeted elsewhere
    const paymentTransactions = transactions.filter(t =>
      t.debt_id === debt.id && t.type === 'payment'
    );
    // Calculate activity: payments are positive activity (money spent to pay off the debt)
    const activity = paymentTransactions.reduce((sum, t) => sum + t.amount, 0);

    // Find if there's a real budget for this debt
    const existingBudget = budgets.find(b => b.name === debt.name && b.category_id === 'virtual-debt-payments');
    const assigned = existingBudget?.assigned_amount || 0;
    const available = assigned - activity;

    return {
      id: `virtual-debt-${debt.id}`,
      name: debt.name,
      category_id: 'virtual-debt-payments',
      assigned_amount: assigned,
      activity,
      available,
      assigned,
      isVirtual: true,
      debt_id: debt.id,
      notes: `Payment for ${debt.name}`
    };
  });

  // Filter out debt payment budgets from the database (they'll be shown as virtual budgets instead)
  const nonDebtBudgets = budgets.filter(b => b.category_id !== 'virtual-debt-payments' && !b.debt_id);

  // Calculate activity and available for each budget
  const budgetsWithActivity = [...nonDebtBudgets, ...debtVirtualBudgets].map(budget => {
    // Skip virtual budgets that were already processed
    if (budget.isVirtual && budget.id.startsWith('virtual-debt-')) {
      return budget;
    }

    // Calculate activity (actual spending) from transactions
    const budgetTransactions = transactions.filter(t =>
      t.budget_id === budget.id && t.type === 'expense'
    );
    const activity = budgetTransactions.reduce((sum, t) => sum + t.amount, 0);

    // Calculate available (assigned - activity)
    const assigned = budget.assigned_amount || 0;
    const available = assigned - activity;

    return {
      ...budget,
      activity,
      available,
      assigned
    };
  });

  // Sort categories by sort_order
  const sortedCategories = [...categories].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  // Group budgets by category
  const categoriesWithBudgets = sortedCategories.map(category => {
    const categoryBudgets = budgetsWithActivity.filter(b => b.category_id === category.id);
    return {
      ...category,
      budgets: categoryBudgets
    };
  });

  // Uncategorized budgets
  const uncategorizedBudgets = budgetsWithActivity.filter(b => !b.category_id);

  // Check if any budget is over-assigned (has negative available)
  const hasOverassignedBudget = budgetsWithActivity.some(b => b.available < 0);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header" onClick={() => setSelectedRow(null)}>
        <div style={{
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 style={{ fontSize: '26px', fontWeight: 700, margin: 0 }}>
              <i className="fas fa-exchange-alt" style={{ marginRight: '10px' }}></i>
              Budget
            </h1>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>
            Zero-based budgeting - Assign every dollar a job
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {/* Ready to Assign - Compact */}
          <div style={{
            background: (readyToAssign >= 0 && !hasOverassignedBudget) ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            borderRadius: 'var(--border-radius)',
            padding: '10px 16px',
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: '140px',
            boxShadow: 'var(--shadow-sm)'
          }}>
            <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {readyToAssign >= 0 && !hasOverassignedBudget ? 'Ready to Assign' : hasOverassignedBudget ? 'Overbudgeted' : 'Overassigned'}
            </div>
            <div style={{ fontSize: '20px', fontWeight: 700 }}>
              {formatCurrency(Math.abs(readyToAssign))}
            </div>
          </div>

          {/* Month Navigation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button className="btn btn-secondary" onClick={handlePrevMonth} style={{ padding: '8px 12px' }}>
              <i className="fas fa-chevron-left"></i>
            </button>
            <span style={{ fontSize: '14px', fontWeight: 600, minWidth: '150px', textAlign: 'center' }}>
              {getMonthName(currentMonth)}
            </span>
            <button className="btn btn-secondary" onClick={handleNextMonth} style={{ padding: '8px 12px' }}>
              <i className="fas fa-chevron-right"></i>
            </button>
            <button className="btn btn-secondary" onClick={handleToday} style={{ padding: '8px 12px' }}>
              Today
            </button>
          </div>
        </div>
      </div>

      {/* Budget Table and Summary Panel */}
      {budgetsWithActivity.length === 0 && categories.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-piggy-bank empty-state-icon"></i>
          <h3 className="empty-state-title">No budgets created yet</h3>
          <p className="empty-state-description">
            Create your first budget to start tracking your spending
          </p>
          <button className="btn btn-primary" onClick={() => setShowBudgetModal(true)} style={{ marginTop: '16px' }}>
            <i className="fas fa-plus"></i>
            Create Your First Budget
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '20px' }}>
          {/* Budget Table */}
          <div className="table-container" style={{ flex: 1 }}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <table className="table" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr>
                  <th style={{ width: '40%', padding: '6px 8px', fontSize: '11px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={handleAddCategory}
                        style={{ fontSize: '10px', padding: '3px 6px' }}
                      >
                        <i className="fas fa-plus" style={{ fontSize: '9px' }}></i> Add Category
                      </button>
                      <span>CATEGORY / BUDGET NAME</span>
                    </div>
                  </th>
                  <th style={{ width: '20%', textAlign: 'right', padding: '6px 8px', fontSize: '11px' }}>ASSIGNED</th>
                  <th style={{ width: '20%', textAlign: 'right', padding: '6px 8px', fontSize: '11px' }}>ACTIVITY</th>
                  <th style={{ width: '15%', textAlign: 'right', padding: '6px 8px', fontSize: '11px' }}>AVAILABLE</th>
                  <th style={{ width: '5%', padding: '6px 8px' }}></th>
                </tr>
              </thead>
              <tbody>
                <SortableContext
                  items={categoriesWithBudgets.filter(c => !c.isVirtual).map(c => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {/* Render categories with their budgets */}
                  {categoriesWithBudgets.map(category => {
                    // For virtual credit cards category, don't make it sortable
                    if (category.isVirtual) {
                      return (
                        <React.Fragment key={category.id}>
                          <tr style={{
                            background: selectedRow?.type === 'category' && selectedRow?.item.id === category.id
                              ? 'var(--primary-light)'
                              : 'var(--bg-secondary)'
                          }}>
                            <td colSpan="5" style={{ padding: '6px 8px', fontWeight: 600, fontSize: '13px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{ flex: 1 }}>
                                  <div
                                    onClick={(e) => handleCategoryClick(e, category)}
                                    style={{
                                      cursor: 'pointer',
                                      userSelect: 'none',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px'
                                    }}
                                  >
                                    <i className={`fas ${category.icon || 'fa-folder'}`} style={{ marginRight: '6px', fontSize: '12px' }}></i>
                                    {category.name}
                                    <span style={{ fontSize: '10px', color: 'var(--text-secondary)', fontStyle: 'italic', marginLeft: '6px' }}>
                                      (Auto-managed)
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                          {/* Budgets under this virtual category */}
                          {category.budgets.map(budget => {
                        // Calculate percentages for progress bar
                        const actualActivityPercent = budget.assigned > 0 ? (budget.activity / budget.assigned) * 100 : 0;
                        const isOverBudget = actualActivityPercent > 100;
                        const activityPercent = Math.min(actualActivityPercent, 100);

                        return (
                          <tr
                            key={budget.id}
                            onClick={(e) => handleBudgetClick(e, budget)}
                            style={{
                              cursor: 'pointer',
                              background: selectedRow?.type === 'budget' && selectedRow?.item.id === budget.id
                                ? 'var(--primary-light)'
                                : 'transparent'
                            }}
                          >
                            <td style={{ paddingLeft: '24px', padding: '4px 8px 4px 24px' }}>
                              <div>
                                <strong style={{ fontSize: '13px' }}>{budget.name}</strong>
                                {budget.notes && (
                                  <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '1px' }}>
                                    {budget.notes}
                                  </div>
                                )}
                              </div>
                              <div style={{ marginTop: '4px', background: 'var(--bg-tertiary)', borderRadius: '3px', overflow: 'hidden', height: '6px', position: 'relative' }}>
                                {/* Progress bar - green until 100%, then red for over-budget */}
                                <div
                                  style={{
                                    width: `${activityPercent}%`,
                                    height: '100%',
                                    background: isOverBudget ? 'var(--danger-color)' : 'var(--success-color)',
                                    transition: 'width 0.3s, background 0.3s'
                                  }}
                                ></div>
                                {/* % overlay */}
                                {budget.assigned > 0 && actualActivityPercent > 0 && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '9px',
                                    fontWeight: 600,
                                    color: 'white',
                                    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                                    pointerEvents: 'none'
                                  }}>
                                    {actualActivityPercent.toFixed(0)}%
                                  </div>
                                )}
                              </div>
                            </td>
                            <td style={{ textAlign: 'right', padding: '4px 8px', fontSize: '13px' }}>
                              <strong>{formatCurrency(budget.assigned)}</strong>
                            </td>
                            <td style={{ textAlign: 'right', padding: '4px 8px', fontSize: '13px' }}>
                              <span style={{ color: 'var(--danger-color)' }}>{formatCurrency(budget.activity)}</span>
                            </td>
                            <td style={{ textAlign: 'right', padding: '4px 8px', fontSize: '13px' }}>
                              <span style={{
                                color: budget.available >= 0 ? 'var(--success-color)' : 'var(--danger-color)',
                                fontWeight: 600
                              }}>
                                {formatCurrency(budget.available)}
                              </span>
                            </td>
                            <td></td>
                          </tr>
                        );
                      })}
                        </React.Fragment>
                      )
                    }

                    // Regular sortable categories
                    return (
                    <SortableCategory
                      key={category.id}
                      category={category}
                      isEditing={editingCategoryId === category.id}
                      isSelected={selectedRow?.type === 'category' && selectedRow?.item.id === category.id}
                      editContent={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <select
                            value={editingCategoryIcon}
                            onChange={(e) => setEditingCategoryIcon(e.target.value)}
                            style={{ padding: '4px', fontSize: '12px', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                          >
                            <option value="fa-folder">📁 Folder</option>
                            <option value="fa-home">🏠 Home</option>
                            <option value="fa-car">🚗 Car</option>
                            <option value="fa-utensils">🍴 Food</option>
                            <option value="fa-shopping-cart">🛒 Shopping</option>
                            <option value="fa-heart">❤️ Health</option>
                            <option value="fa-plane">✈️ Travel</option>
                            <option value="fa-graduation-cap">🎓 Education</option>
                            <option value="fa-film">🎬 Entertainment</option>
                            <option value="fa-bolt">⚡ Utilities</option>
                          </select>
                          <input
                            type="text"
                            value={editingCategoryName}
                            onChange={(e) => setEditingCategoryName(e.target.value)}
                            style={{ flex: 1, padding: '6px 10px', fontSize: '13px', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveCategory(category.id);
                              if (e.key === 'Escape') handleCancelEditCategory();
                            }}
                          />
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleSaveCategory(category.id)}
                            style={{ padding: '4px 12px', fontSize: '12px' }}
                          >
                            <i className="fas fa-check"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={handleCancelEditCategory}
                            style={{ padding: '4px 12px', fontSize: '12px' }}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      }
                      normalContent={
                        <div
                          onClick={(e) => handleCategoryClick(e, category)}
                          style={{
                            cursor: 'pointer',
                            userSelect: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                          onMouseEnter={() => setHoveredCategoryId(category.id)}
                          onMouseLeave={() => setHoveredCategoryId(null)}
                        >
                          <div>
                            <i className={`fas ${category.icon || 'fa-folder'}`} style={{ marginRight: '8px' }}></i>
                            {category.name}
                          </div>
                          {hoveredCategoryId === category.id && (
                            <button
                              onClick={(e) => handleCreateBudgetForCategory(e, category.id)}
                              className="btn btn-sm btn-primary"
                              style={{ padding: '0px 4px', fontSize: '10px' }}
                            >
                              <i className="fas fa-plus"></i>
                            </button>
                          )}
                        </div>
                      }
                    >
                      {/* Budgets under this category */}
                      {category.budgets.map(budget => {
                    // Calculate percentages for progress bar
                    const actualActivityPercent = budget.assigned > 0 ? (budget.activity / budget.assigned) * 100 : 0;
                    const isOverBudget = actualActivityPercent > 100;
                    const activityPercent = Math.min(actualActivityPercent, 100);

                    return (
                      <tr
                        key={budget.id}
                        onClick={(e) => handleBudgetClick(e, budget)}
                        style={{
                          cursor: 'pointer',
                          background: selectedRow?.type === 'budget' && selectedRow?.item.id === budget.id
                            ? 'var(--primary-light)'
                            : 'transparent'
                        }}
                      >
                        <td style={{ paddingLeft: '24px', padding: '4px 8px 4px 24px' }}>
                          <div>
                            <strong style={{ fontSize: '13px' }}>{budget.name}</strong>
                            {budget.notes && (
                              <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '1px' }}>
                                {budget.notes}
                              </div>
                            )}
                          </div>
                          <div style={{ marginTop: '4px', background: 'var(--bg-tertiary)', borderRadius: '3px', overflow: 'hidden', height: '6px', position: 'relative' }}>
                            {/* Progress bar - green until 100%, then red for over-budget */}
                            <div
                              style={{
                                width: `${activityPercent}%`,
                                height: '100%',
                                background: isOverBudget ? 'var(--danger-color)' : 'var(--success-color)',
                                transition: 'width 0.3s, background 0.3s'
                              }}
                            ></div>
                            {/* % overlay */}
                            {budget.assigned > 0 && actualActivityPercent > 0 && (
                              <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '9px',
                                fontWeight: 600,
                                color: 'white',
                                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                                pointerEvents: 'none'
                              }}>
                                {actualActivityPercent.toFixed(0)}%
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ textAlign: 'right', padding: '4px 8px', fontSize: '13px' }}>
                          <strong>{formatCurrency(budget.assigned)}</strong>
                        </td>
                        <td style={{ textAlign: 'right', padding: '4px 8px', fontSize: '13px' }}>
                          <span style={{ color: 'var(--danger-color)' }}>{formatCurrency(budget.activity)}</span>
                        </td>
                        <td style={{ textAlign: 'right', padding: '4px 8px', fontSize: '13px' }}>
                          <span style={{
                            color: budget.available >= 0 ? 'var(--success-color)' : 'var(--danger-color)',
                            fontWeight: 600
                          }}>
                            {formatCurrency(budget.available)}
                          </span>
                        </td>
                        <td></td>
                      </tr>
                    );
                  })}
                    </SortableCategory>
                    );
                  })}
                </SortableContext>

                {/* Uncategorized budgets */}
              {uncategorizedBudgets.length > 0 && (
                <>
                  <tr style={{ background: 'var(--bg-secondary)' }}>
                    <td colSpan="5" style={{ padding: '6px 8px', fontWeight: 600, fontSize: '13px' }}>
                      <i className="fas fa-question-circle" style={{ marginRight: '6px', fontSize: '12px' }}></i>
                      Uncategorized
                    </td>
                  </tr>
                  {uncategorizedBudgets.map(budget => {
                    // Calculate percentages for progress bar
                    const actualActivityPercent = budget.assigned > 0 ? (budget.activity / budget.assigned) * 100 : 0;
                    const isOverBudget = actualActivityPercent > 100;
                    const activityPercent = Math.min(actualActivityPercent, 100);

                    return (
                      <tr
                        key={budget.id}
                        onClick={(e) => handleBudgetClick(e, budget)}
                        style={{
                          cursor: 'pointer',
                          background: selectedRow?.type === 'budget' && selectedRow?.item.id === budget.id
                            ? 'var(--primary-light)'
                            : 'transparent'
                        }}
                      >
                        <td style={{ paddingLeft: '32px' }}>
                          <div>
                            <strong>{budget.name}</strong>
                            {budget.notes && (
                              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                                {budget.notes}
                              </div>
                            )}
                          </div>
                          <div style={{ marginTop: '8px', background: 'var(--bg-tertiary)', borderRadius: '4px', overflow: 'hidden', height: '8px', position: 'relative' }}>
                            {/* Progress bar - green until 100%, then red for over-budget */}
                            <div
                              style={{
                                width: `${activityPercent}%`,
                                height: '100%',
                                background: isOverBudget ? 'var(--danger-color)' : 'var(--success-color)',
                                transition: 'width 0.3s, background 0.3s'
                              }}
                            ></div>
                            {/* % overlay */}
                            {budget.assigned > 0 && actualActivityPercent > 0 && (
                              <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '10px',
                                fontWeight: 600,
                                color: 'white',
                                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                                pointerEvents: 'none'
                              }}>
                                {actualActivityPercent.toFixed(0)}%
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <strong>{formatCurrency(budget.assigned)}</strong>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <span style={{ color: 'var(--danger-color)' }}>{formatCurrency(budget.activity)}</span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <span style={{
                            color: budget.available >= 0 ? 'var(--success-color)' : 'var(--danger-color)',
                            fontWeight: 600
                          }}>
                            {formatCurrency(budget.available)}
                          </span>
                        </td>
                        <td></td>
                      </tr>
                    );
                  })}
                </>
              )}

              {/* Add Category Row - only show when adding */}
              {isAddingCategory && (
                <tr style={{ background: 'var(--bg-secondary)' }}>
                  <td colSpan="5" style={{ padding: '6px 8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <select
                        value={editingCategoryIcon}
                        onChange={(e) => setEditingCategoryIcon(e.target.value)}
                        style={{ padding: '4px', fontSize: '12px', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                      >
                        <option value="fa-folder">📁 Folder</option>
                        <option value="fa-home">🏠 Home</option>
                        <option value="fa-car">🚗 Car</option>
                        <option value="fa-utensils">🍴 Food</option>
                        <option value="fa-shopping-cart">🛒 Shopping</option>
                        <option value="fa-heart">❤️ Health</option>
                        <option value="fa-plane">✈️ Travel</option>
                        <option value="fa-graduation-cap">🎓 Education</option>
                        <option value="fa-film">🎬 Entertainment</option>
                        <option value="fa-bolt">⚡ Utilities</option>
                      </select>
                      <input
                        type="text"
                        value={editingCategoryName}
                        onChange={(e) => setEditingCategoryName(e.target.value)}
                        placeholder="Category name..."
                        style={{ flex: 1, padding: '6px 10px', fontSize: '13px', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveNewCategory();
                          if (e.key === 'Escape') handleCancelAddCategory();
                        }}
                      />
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={handleSaveNewCategory}
                        style={{ padding: '4px 12px', fontSize: '12px' }}
                      >
                        <i className="fas fa-check"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={handleCancelAddCategory}
                        style={{ padding: '4px 12px', fontSize: '12px' }}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
            </DndContext>
          </div>

          {/* Summary Panel */}
        <div
          style={{
            width: '350px',
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--border-radius)',
            padding: '20px',
            maxHeight: '600px',
            overflowY: 'auto'
          }}
          onClick={(e) => {
            // Check if we're clicking on a button or the category breakdown items
            const isButton = e.target.closest('button');
            const isCategoryItem = e.target.closest('[data-category-item]');

            // Only deselect if NOT clicking on buttons or category items
            if (!isButton && !isCategoryItem) {
              setSelectedRow(null);
            }
          }}
        >
          {selectedRow ? (
            selectedRow.type === 'category' ? (
              // Category Summary
              <div>
                <h3 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: 600 }}>
                  <i className={`fas ${selectedRow.item.icon || 'fa-folder'}`} style={{ marginRight: '8px' }}></i>
                  {selectedRow.item.name}
                </h3>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                  <button
                    onClick={() => handleEditCategory(selectedRow.item)}
                    className="btn btn-secondary"
                    style={{ flex: 1, fontSize: '12px', padding: '8px' }}
                  >
                    <i className="fas fa-edit"></i> Edit
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(selectedRow.item.id)}
                    className="btn btn-danger"
                    style={{ flex: 1, fontSize: '12px', padding: '8px' }}
                  >
                    <i className="fas fa-trash"></i> Delete
                  </button>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>CATEGORY TOTALS</div>
                  {(() => {
                    const categoryBudgets = budgetsWithActivity.filter(b => b.category_id === selectedRow.item.id);
                    const totalAssigned = categoryBudgets.reduce((sum, b) => sum + b.assigned, 0);
                    const totalActivity = categoryBudgets.reduce((sum, b) => sum + b.activity, 0);
                    const totalAvailable = categoryBudgets.reduce((sum, b) => sum + b.available, 0);

                    return (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-color)' }}>
                          <span>Assigned</span>
                          <strong>{formatCurrency(totalAssigned)}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-color)' }}>
                          <span>Activity</span>
                          <span style={{ color: 'var(--danger-color)', fontWeight: 600 }}>{formatCurrency(totalActivity)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                          <span>Available</span>
                          <span style={{
                            color: totalAvailable >= 0 ? 'var(--success-color)' : 'var(--danger-color)',
                            fontWeight: 600
                          }}>{formatCurrency(totalAvailable)}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>BUDGETS IN CATEGORY</div>
                  {categoriesWithBudgets.find(c => c.id === selectedRow.item.id)?.budgets.map(budget => (
                    <div key={budget.id} style={{
                      padding: '8px',
                      background: 'var(--bg-secondary)',
                      borderRadius: '4px',
                      marginBottom: '6px',
                      fontSize: '12px'
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: '4px' }}>{budget.name}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Available:</span>
                        <span style={{
                          color: budget.available >= 0 ? 'var(--success-color)' : 'var(--danger-color)',
                          fontWeight: 600
                        }}>{formatCurrency(budget.available)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              // Budget Summary
              <div>
                <h3 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: 600 }}>
                  {selectedRow.item.name}
                </h3>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                  <button
                    onClick={() => openAssignModal(selectedRow.item)}
                    className="btn btn-primary"
                    style={{ flex: 1, fontSize: '12px', padding: '8px' }}
                  >
                    <i className="fas fa-hand-holding-usd"></i> Assign
                  </button>
                  <button
                    onClick={() => handleDeleteBudget(selectedRow.item.id)}
                    className="btn btn-danger"
                    style={{ flex: 1, fontSize: '12px', padding: '8px' }}
                  >
                    <i className="fas fa-trash"></i> Delete
                  </button>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border-color)' }}>
                    <span>Assigned</span>
                    <strong>{formatCurrency(selectedRow.item.assigned)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border-color)' }}>
                    <span>Activity</span>
                    <span style={{ color: 'var(--danger-color)', fontWeight: 600 }}>{formatCurrency(selectedRow.item.activity)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0' }}>
                    <span>Available</span>
                    <span style={{
                      color: selectedRow.item.available >= 0 ? 'var(--success-color)' : 'var(--danger-color)',
                      fontWeight: 600
                    }}>{formatCurrency(selectedRow.item.available)}</span>
                  </div>
                </div>

                {/* Quick Assign Options */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>QUICK ASSIGN</div>
                  {(() => {
                    const underfunded = selectedRow.item.available < 0 ? Math.abs(selectedRow.item.available) : 0;

                    // For now, show placeholders for historical data
                    // TODO: Fetch last month's data from API
                    const assignedLastMonth = 0;
                    const averageAssigned = 0;
                    const spentLastMonth = 0;

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {underfunded > 0 && (
                          <button
                            onClick={() => handleQuickAssign(selectedRow.item, underfunded)}
                            style={{
                              padding: '8px 12px',
                              background: 'var(--danger-light)',
                              border: '1px solid var(--danger-color)',
                              borderRadius: 'var(--border-radius)',
                              cursor: 'pointer',
                              fontSize: '12px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--danger-color)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'var(--danger-light)'}
                          >
                            <span>Underfunded</span>
                            <strong>{formatCurrency(underfunded)}</strong>
                          </button>
                        )}
                        {assignedLastMonth > 0 && (
                          <button
                            onClick={() => handleQuickAssign(selectedRow.item, assignedLastMonth)}
                            style={{
                              padding: '8px 12px',
                              background: 'var(--bg-secondary)',
                              border: '1px solid var(--border-color)',
                              borderRadius: 'var(--border-radius)',
                              cursor: 'pointer',
                              fontSize: '12px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--primary-light)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                          >
                            <span>Assigned Last Month</span>
                            <strong>{formatCurrency(assignedLastMonth)}</strong>
                          </button>
                        )}
                        {averageAssigned > 0 && (
                          <button
                            onClick={() => handleQuickAssign(selectedRow.item, averageAssigned)}
                            style={{
                              padding: '8px 12px',
                              background: 'var(--bg-secondary)',
                              border: '1px solid var(--border-color)',
                              borderRadius: 'var(--border-radius)',
                              cursor: 'pointer',
                              fontSize: '12px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--primary-light)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                          >
                            <span>Average Assigned</span>
                            <strong>{formatCurrency(averageAssigned)}</strong>
                          </button>
                        )}
                        {spentLastMonth > 0 && (
                          <button
                            onClick={() => handleQuickAssign(selectedRow.item, spentLastMonth)}
                            style={{
                              padding: '8px 12px',
                              background: 'var(--bg-secondary)',
                              border: '1px solid var(--border-color)',
                              borderRadius: 'var(--border-radius)',
                              cursor: 'pointer',
                              fontSize: '12px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--primary-light)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                          >
                            <span>Spent Last Month</span>
                            <strong>{formatCurrency(spentLastMonth)}</strong>
                          </button>
                        )}
                        {underfunded === 0 && assignedLastMonth === 0 && averageAssigned === 0 && spentLastMonth === 0 && (
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic', textAlign: 'center', padding: '16px 0' }}>
                            No quick assign suggestions available
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {selectedRow.item.notes && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>NOTES</div>
                    <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{selectedRow.item.notes}</div>
                  </div>
                )}

                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>RECENT TRANSACTIONS</div>
                  {(() => {
                    const budgetTransactions = transactions.filter(t =>
                      t.budget_id === selectedRow.item.id ||
                      (selectedRow.item.debt_id && t.debt_id === selectedRow.item.debt_id)
                    ).slice(0, 5);
                    if (budgetTransactions.length === 0) {
                      return <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>No transactions yet</div>;
                    }
                    return budgetTransactions.map(tx => (
                      <div key={tx.id} style={{
                        padding: '8px',
                        background: 'var(--bg-secondary)',
                        borderRadius: '4px',
                        marginBottom: '6px',
                        fontSize: '12px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                          <span style={{ fontWeight: 600 }}>{tx.description || 'Transaction'}</span>
                          <span style={{ color: 'var(--danger-color)', fontWeight: 600 }}>{formatCurrency(tx.amount)}</span>
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                          {new Date(tx.date).toLocaleDateString()}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )
          ) : (
            // Monthly Summary - Default View
            <div>
              <h3 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: 600 }}>
                <i className="fas fa-calendar-alt" style={{ marginRight: '8px' }}></i>
                {getMonthName(currentMonth)}
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  MONTHLY OVERVIEW
                </div>

                {/* Income */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '12px 0',
                  borderBottom: '1px solid var(--border-color)'
                }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '2px' }}>Income</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                      {incomeTransactions.length} transaction{incomeTransactions.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <strong style={{ color: 'var(--success-color)', fontSize: '16px' }}>
                    {formatCurrency(totalIncome)}
                  </strong>
                </div>

                {/* Assigned */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '12px 0',
                  borderBottom: '1px solid var(--border-color)'
                }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '2px' }}>Assigned</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                      {budgetsWithActivity.filter(b => b.assigned > 0).length} budget{budgetsWithActivity.filter(b => b.assigned > 0).length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <strong style={{ fontSize: '16px' }}>
                    {formatCurrency(totalAssigned)}
                  </strong>
                </div>

                {/* Activity */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '12px 0',
                  borderBottom: '1px solid var(--border-color)'
                }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '2px' }}>Activity</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                      Total spent this month
                    </div>
                  </div>
                  <strong style={{ color: 'var(--danger-color)', fontSize: '16px' }}>
                    {formatCurrency(budgetsWithActivity.reduce((sum, b) => sum + b.activity, 0))}
                  </strong>
                </div>

                {/* Available */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '12px 0'
                }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '2px' }}>Available</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                      Across all budgets
                    </div>
                  </div>
                  <strong style={{
                    color: budgetsWithActivity.reduce((sum, b) => sum + b.available, 0) >= 0 ? 'var(--success-color)' : 'var(--danger-color)',
                    fontSize: '16px'
                  }}>
                    {formatCurrency(budgetsWithActivity.reduce((sum, b) => sum + b.available, 0))}
                  </strong>
                </div>
              </div>

              {/* Category Breakdown */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  CATEGORY BREAKDOWN
                </div>
                {categoriesWithBudgets.filter(c => c.budgets.length > 0).map(category => {
                  const totalAssigned = category.budgets.reduce((sum, b) => sum + b.assigned, 0);
                  const totalActivity = category.budgets.reduce((sum, b) => sum + b.activity, 0);
                  const totalAvailable = category.budgets.reduce((sum, b) => sum + b.available, 0);

                  return (
                    <div
                      key={category.id}
                      data-category-item
                      style={{
                        padding: '10px',
                        background: 'var(--bg-secondary)',
                        borderRadius: '6px',
                        marginBottom: '8px',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCategoryClick(e, category);
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--primary-light)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <div style={{ fontWeight: 600, fontSize: '13px' }}>
                          <i className={`fas ${category.icon || 'fa-folder'}`} style={{ marginRight: '6px' }}></i>
                          {category.name}
                        </div>
                        <span style={{
                          color: totalAvailable >= 0 ? 'var(--success-color)' : 'var(--danger-color)',
                          fontWeight: 600,
                          fontSize: '13px'
                        }}>
                          {formatCurrency(totalAvailable)}
                        </span>
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', gap: '12px' }}>
                        <span>Assigned: {formatCurrency(totalAssigned)}</span>
                        <span>Spent: {formatCurrency(totalActivity)}</span>
                      </div>
                    </div>
                  );
                })}
                {categoriesWithBudgets.filter(c => c.budgets.length > 0).length === 0 && (
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic', textAlign: 'center', padding: '20px 0' }}>
                    No categories with budgets yet
                  </div>
                )}
              </div>

              <div style={{ textAlign: 'center', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>
                  Click a category or budget to see details
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Modals */}
      {showBudgetModal && (
        <AddBudgetModal
          availableAmount={readyToAssign}
          currentMonth={currentMonth}
          preselectedCategoryId={preselectedCategoryId}
          onClose={() => {
            setShowBudgetModal(false);
            setPreselectedCategoryId(null);
          }}
          onSave={() => {
            setShowBudgetModal(false);
            setPreselectedCategoryId(null);
            fetchData();
          }}
        />
      )}

      {/* Assign Money Modal */}
      {showAssignModal && assigningBudget && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-hand-holding-usd" style={{ marginRight: '12px', color: 'var(--primary-color)' }}></i>
                Assign Money
              </h2>
              <button className="modal-close-btn" onClick={() => setShowAssignModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div style={{ marginBottom: '20px', padding: '16px', background: 'var(--bg-secondary)', borderRadius: 'var(--border-radius)' }}>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  Budget
                </div>
                <div style={{ fontSize: '18px', fontWeight: 700 }}>
                  {assigningBudget.name}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px' }}>
                  Currently assigned: {formatCurrency(assigningBudget.assigned_amount || 0)}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--danger-color)', marginTop: '4px' }}>
                  Available: {formatCurrency(assigningBudget.available)}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Amount to Assign</label>
                <input
                  type="number"
                  className="form-control"
                  value={assignAmount}
                  onChange={(e) => setAssignAmount(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  autoFocus
                />
                <small style={{ color: 'var(--text-secondary)', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                  Available to assign: {formatCurrency(readyToAssign)}
                </small>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button className="btn btn-secondary" onClick={() => setShowAssignModal(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleAssignMoney}
                  disabled={!assignAmount || parseFloat(assignAmount) <= 0}
                >
                  <i className="fas fa-check"></i>
                  Assign {assignAmount ? formatCurrency(parseFloat(assignAmount)) : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Popup Menu */}
      {popupMenu && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            left: `${popupMenu.x}px`,
            top: `${popupMenu.y}px`,
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--border-radius)',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 1000,
            minWidth: '180px'
          }}
        >
          {popupMenu.type === 'category' ? (
            <div>
              <button
                onClick={() => {
                  handleEditCategory(popupMenu.item);
                  setPopupMenu(null);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <i className="fas fa-edit" style={{ width: '16px' }}></i>
                Edit Category
              </button>
              <button
                onClick={() => {
                  handleDeleteCategory(popupMenu.item.id);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: 'var(--danger-color)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--danger-light)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <i className="fas fa-trash" style={{ width: '16px' }}></i>
                Delete Category
              </button>
            </div>
          ) : (
            <div>
              <button
                onClick={() => {
                  openAssignModal(popupMenu.item);
                  setPopupMenu(null);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <i className="fas fa-hand-holding-usd" style={{ width: '16px' }}></i>
                Assign Money
              </button>
              <button
                onClick={() => {
                  handleDeleteBudget(popupMenu.item.id);
                  setPopupMenu(null);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: 'var(--danger-color)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--danger-light)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <i className="fas fa-trash" style={{ width: '16px' }}></i>
                Delete Budget
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Budget;
